import { Command } from 'commander';
import { ApiClient } from '../client.js';
import { formatOutput } from '../output.js';
import { getDatabase, getFormat, mcpToolsetUrl } from '../helpers.js';
import { requireActiveOrg } from '../require-active-org.js';

export const tokensCommand = new Command('tokens')
  .description('Manage API tokens');

tokensCommand.command('create')
  .description('Create a scoped API token')
  .requiredOption('--name <name>', 'Token name')
  .requiredOption('--grants <json>', 'Grant definitions as JSON array')
  .option('--expires-at <date>', 'Expiration date (ISO 8601, e.g., 2026-06-01T00:00:00Z)')
  .action(async function (this: Command, opts: { name: string; grants: string; expiresAt?: string }) {
    await requireActiveOrg();
    const client = new ApiClient();
    let grants: unknown;
    try {
      grants = JSON.parse(opts.grants);
    } catch {
      throw new Error('--grants must be valid JSON');
    }
    const body: Record<string, unknown> = { name: opts.name, grants };
    if (opts.expiresAt) body.expires_at = opts.expiresAt;
    const data = await client.request('POST', '/v1/tokens', body);
    console.log(formatOutput(data, getFormat(this)));
  });

tokensCommand.command('create-for-toolset <slug>')
  .description('Create a toolset-bound (consumer) token whose authorization IS the toolset tool surface — it can call exactly the tools that toolset exposes (those collections + operations, relationships, batch, and sql only if enabled) and nothing else. Binds to the configured --database.')
  .option('--name <name>', 'Token name (default: "<slug> consumer")')
  .option('--expires-at <date>', 'Expiration date (ISO 8601, e.g., 2026-06-01T00:00:00Z)')
  .action(async function (this: Command, slug: string, opts: { name?: string; expiresAt?: string }) {
    await requireActiveOrg();
    const database = getDatabase(this);
    const client = new ApiClient();
    const body: Record<string, unknown> = {
      name: opts.name ?? `${slug} consumer`,
      toolset_binding: { database_id: database, toolset_id: slug },
    };
    if (opts.expiresAt) body.expires_at = opts.expiresAt;
    const data = await client.request('POST', '/v1/tokens', body);
    const format = getFormat(this);
    console.log(formatOutput(data, format));
    // "create the server, here's its token and URL"
    if (format !== 'json') {
      console.log(`\nMCP URL: ${mcpToolsetUrl(slug)}`);
    }
  });

tokensCommand.command('list')
  .description('List API tokens')
  .action(async function (this: Command) {
    const client = new ApiClient();
    const data = await client.request('GET', '/v1/tokens');
    console.log(formatOutput(data, getFormat(this)));
  });

tokensCommand.command('revoke <token_id>')
  .description('Revoke an API token')
  .action(async function (this: Command, tokenId: string) {
    const client = new ApiClient();
    await client.request('DELETE', `/v1/tokens/${tokenId}`);
    console.log('Token revoked');
  });
